// ═══════════════════════════════════════════════════════════════════════════
// PAGE: ÉDITION D'UNE ACTION (RefactoUX/Actions — mai 2026)
// ═══════════════════════════════════════════════════════════════════════════
//
// Route : `#action/:id` — édition d'une action existante depuis la liste
// globale (id, description, crayon ✎). Réutilise le composant
// `renderActionEditor` (component-action-modal/index.ts) pour garantir un
// comportement identique entre la page et la modale d'édition (encore
// utilisée depuis d'autres entrées : notifications, post-its, projet,
// import, comités, bascule).
//
// Le breadcrumb en haut du contenu ramène à la liste des actions. Les
// boutons "Enregistrer / Enregistrer sans notifier / Annuler" vivent dans
// l'en-tête de page (zone #page-actions), comme les autres pages d'édition.

import { definePage } from '../router.js';
import { markLastModified } from '../utils/last-modified-anim.js';
import {
  buildCreateActionPayload,
  type EditorSavedAction,
} from '../domain/action-create-payload.js';

interface PageActionUser {
  id: number;
  trigram?: string | null;
  name: string;
  is_team?: boolean;
}

interface PageActionStatus {
  id: number;
  name: string;
  position: number;
}

interface PageActionProject {
  id: number;
  code?: string | null;
  name: string;
}

interface ActionFromApi {
  id: number;
  text?: string;
  deadline?: string | null;
  deadline_revised?: string | null;
  status_id?: number | null;
  progress?: number;
  priority?: number | null;
  expected_deliverable?: string | null;
  external_link?: string | null;
  approver_id?: number | null;
  performers?: number[] | Array<{ id: number }>;
  consulted?: number[] | Array<{ id: number }>;
  informed?: number[] | Array<{ id: number }>;
  project_ids?: number[];
  committee_ids?: number[];
  structure_ids?: number[];
  escalated_structure_ids?: number[];
  is_confidential?: boolean;
  origins?: Array<{ project_name?: string; risk_code?: string; risk_category?: string }>;
  merged_into_id?: number | null;
  merged_into_text?: string | null;
}

// L'API renvoie parfois les RACI sous forme de tableaux d'objets {id,…} (jointures
// SQLAlchemy) ou de tableaux d'ids (sérialiseurs allégés). On normalise pour
// que renderActionEditor reçoive uniquement des ids — c'est ce qu'il attend.
function _normalizeIds(v: unknown): number[] {
  if (!Array.isArray(v)) return [];
  return v
    .map((x) => (typeof x === 'number' ? x : ((x as { id?: number }).id ?? 0)))
    .filter((n) => n > 0);
}

// Hash de retour posé par la page appelante (liste actions), consommé une fois.
function _consumeActionBackHash(): string {
  try {
    const stored = sessionStorage.getItem('actionEditor:backHash');
    if (stored) {
      sessionStorage.removeItem('actionEditor:backHash');
      if (stored.startsWith('#') && stored !== window.location.hash) return stored;
    }
  } catch {
    /* noop */
  }
  return '#actions';
}

// Copie d'action posée par le CTA « Dupliquer » de la barre groupée (#actions).
// Consommée une fois puis effacée. Clé partagée avec page-actions/render-page.ts.
const ACTION_DUPLICATE_PREFILL_KEY = 'actionEditor:duplicatePrefill';
function _consumeDuplicatePrefill(): Partial<ActionFromApi> | null {
  try {
    const raw = sessionStorage.getItem(ACTION_DUPLICATE_PREFILL_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(ACTION_DUPLICATE_PREFILL_KEY);
    return JSON.parse(raw) as Partial<ActionFromApi>;
  } catch {
    return null;
  }
}

pages.action = definePage<'action/:id'>(async ({ id }) => {
  // Le routeur ne remet pas le défilement à zéro : arrivé depuis une page
  // scrollée (la fiche réunion et son CTA de contrôle, par exemple), le bas de
  // l'éditeur — donc « Enregistrer » — se retrouvait sous le pli.
  window.scrollTo(0, 0);
  setHeader(t('common.loading'), '');
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);

  const uid = await ensureCurrentUserId();
  if (!uid) {
    setContent(`<div class="error">${esc(t('pageActions.notAuth'))}</div>`);
    return;
  }

  // ── Mode création / duplication (id=0) ───────────────────────────────────
  // Ouvert par le CTA « Dupliquer » de la barre groupée #actions (en page,
  // pas en modale). On héberge le même éditeur, mais avec une action sans id :
  // l'éditeur passe en mode « nouvelle action » et délègue la création à
  // onSaved (cf. renderActionEditor : `if (isNew) onSaved(...)`).
  if (id === 0) {
    let users: PageActionUser[];
    let statuses: PageActionStatus[];
    let projects: PageActionProject[];
    try {
      const [allUsers, allStatuses, allProjects] = (await Promise.all([
        loadUsers(),
        loadActionStatuses(),
        api.getProjects(),
      ])) as [PageActionUser[], PageActionStatus[], PageActionProject[]];
      users = allUsers;
      statuses = allStatuses;
      projects = allProjects;
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : t('common.error');
      setContent(`<div class="error">${esc(msg)}</div>`);
      return;
    }

    // Copie posée par « Dupliquer » (entête + RACI + projets + comités, sans
    // origines) ou par le CTA d'un contrôle de réunion. Sans copie → création
    // vierge.
    const copy = (_consumeDuplicatePrefill() ?? {}) as ActionFromApi;
    // Guide de réunion d'origine, s'il y en a un : une fois l'action
    // enregistrée, on le marque pour ne plus reproposer le CTA. Capturé ici,
    // le pré-remplissage étant consommé (et effacé) à la lecture.
    // Champs propres au pré-remplissage venu d'un guide de réunion : ils ne
    // font pas partie du modèle d'action renvoyé par l'API.
    const fromMeeting = copy as {
      meeting_guide?: { meeting_id: number; guide_id: number };
      traceability_note?: string;
    };
    const fromGuide = fromMeeting.meeting_guide;

    setHeader(esc(t('actionModal.title.new')), '');
    setContent(
      `<div style="height:calc(100vh - 56px - 48px);min-height:520px">
         <div id="ea-editor-host" style="height:100%"></div>
       </div>`,
    );
    const hostNew = document.getElementById('ea-editor-host');
    if (!hostNew) return;
    const breadcrumbNew = `<nav class="ea-breadcrumb" aria-label="${esc(t('pageAction.breadcrumbAria'))}" style="font-size:12px;color:var(--text2)">
         <a href="#actions" style="color:var(--accent);text-decoration:underline">${esc(t('vocabulary.action_plural'))}</a>
         <span style="color:var(--text3);margin:0 6px">›</span>
         <span style="color:var(--text2);font-weight:500">${esc(t('actionModal.title.new'))}</span>
       </nav>`;

    // `startDirty` : arrivé avec un pré-remplissage, tout est déjà en place.
    // Exiger une saisie pour faire apparaître « Enregistrer » n'ajouterait
    // qu'un geste inutile — on veut pouvoir enregistrer sans rien toucher.
    const prefilled = Object.keys(copy).length > 0;
    window.renderActionEditor(hostNew, copy as never, {
      users: users as never,
      statuses: statuses as never,
      projects: projects as never,
      stickyTopPrefixHtml: breadcrumbNew,
      startDirty: prefilled,
      onSaved: (saved: unknown, notes: unknown, notify?: boolean) => {
        void (async () => {
          const d = saved as ActionFromApi;
          try {
            const created = (await api.createAction(
              buildCreateActionPayload(d as EditorSavedAction, {
                structureIdsFallback: copy.structure_ids,
              }),
              { notify: notify !== false },
            )) as { id?: number };
            toast(t('common.saved'), 'ok');
            if (created?.id) markLastModified('actions', created.id);
            // Notes saisies avant l'enregistrement : l'éditeur les accumule
            // (`_pendingNotes`) et nous les rend ici. Sans ce POST, la note
            // pré-remplie à la création était simplement perdue.
            const pending = (notes as string[] | undefined) || [];
            if (created?.id) {
              for (const comment of pending.filter((n) => n && n.trim())) {
                try {
                  await api.addActionNote(created.id, { comment });
                } catch {
                  /* l'action est créée : ne pas alarmer sur une note */
                }
              }
            }
            // Marque le guide d'origine. En cas d'échec on ne dit rien et on
            // ne bloque pas le retour : l'action est bien créée, le pire est
            // qu'on repropose le CTA — un doublon possible, jamais une perte.
            if (fromGuide) {
              try {
                await api.markGuideActionCreated(fromGuide.meeting_id, fromGuide.guide_id);
              } catch {
                /* l'action est enregistrée : ne pas alarmer sur le drapeau */
              }
            }
            window.location.hash = _consumeActionBackHash();
          } catch (err: unknown) {
            toast(err instanceof Error ? err.message : t('common.error'), 'error');
          }
        })();
      },
      onCancel: () => {
        window.location.hash = _consumeActionBackHash();
      },
    });
    // Note d'origine (création depuis un contrôle de réunion) : posée dans la
    // zone de saisie plutôt qu'envoyée en douce, pour rester visible et
    // modifiable avant enregistrement.
    if (fromMeeting.traceability_note) {
      const noteEl = document.getElementById('new-note-text') as HTMLTextAreaElement | null;
      if (noteEl) noteEl.value = fromMeeting.traceability_note;
    }
    return;
  }

  // Charge l'action ciblée + référentiels en parallèle. `getAction` lit une
  // seule action (drafts inclus) au lieu de télécharger toute la liste. Un 404
  // (introuvable OU hors périmètre RBAC) est rabattu sur `undefined` pour la
  // branche "introuvable" ci-dessous, sans erreur générique.
  let action: ActionFromApi | undefined;
  let users: PageActionUser[];
  let statuses: PageActionStatus[];
  let projects: PageActionProject[];
  try {
    const [fetchedAction, allUsers, allStatuses, allProjects] = (await Promise.all([
      api.getAction(id).catch((e: unknown) => {
        const m = e instanceof Error ? e.message : '';
        if (/not found/i.test(m)) return undefined;
        throw e;
      }),
      loadUsers(),
      loadActionStatuses(),
      api.getProjects(),
    ])) as [ActionFromApi | undefined, PageActionUser[], PageActionStatus[], PageActionProject[]];
    action = fetchedAction;
    users = allUsers;
    statuses = allStatuses;
    projects = allProjects;
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
    return;
  }

  if (!action) {
    setHeader(t('vocabulary.action'), '');
    setContent(
      `<div class="error" style="padding:20px">
        <p>${esc(t('pageAction.notFound', { id }))}</p>
        <p style="margin-top:12px"><a href="#actions" class="btn btn-secondary">← ${esc(t('vocabulary.action_plural'))}</a></p>
      </div>`,
    );
    return;
  }

  // Action absorbée par une fusion : page figée en lecture seule, on bypass
  // l'éditeur (qui supposerait un état modifiable) et on propose un saut
  // vers la conservée. Le backend refuse toute mutation côté serveur ; la
  // garde côté front évite l'expérience cassée d'un éditeur "vide".
  if (action.merged_into_id) {
    const keeperId = action.merged_into_id;
    setHeader(esc(t('actionModal.title.edit', { id })), '');
    setContent(
      `<div style="max-width:720px;margin:24px auto;padding:0 12px">
        <nav style="font-size:12px;color:var(--text2);margin-bottom:12px">
          <a href="#actions" style="color:var(--accent);text-decoration:underline">${esc(t('vocabulary.action_plural'))}</a>
          <span style="color:var(--text3);margin:0 6px">›</span>
          <span>#${id}</span>
        </nav>
        <div style="padding:14px 16px;background:#eef6ff;border:1px solid #4b8fe3;border-radius:6px;color:#1d3a66;line-height:1.55">
          ${esc(t('pageAction.merged.banner', { id: keeperId }))}
        </div>
        <div style="margin-top:14px">
          <a href="#action/${keeperId}" class="btn btn-primary">${esc(t('pageAction.merged.bannerCta'))} #${keeperId}</a>
        </div>
        ${action.text ? `<div style="margin-top:18px;padding:12px;background:var(--bg2);border-radius:6px"><div style="font-size:11px;color:var(--text3);text-transform:uppercase;margin-bottom:4px">${esc(t('pageActionsMerge.field.text'))}</div><div>${esc(action.text)}</div></div>` : ''}
      </div>`,
    );
    return;
  }

  // Normalisation des RACI (l'éditeur attend des ids).
  const normalized: ActionFromApi = {
    ...action,
    performers: _normalizeIds(action.performers),
    consulted: _normalizeIds(action.consulted),
    informed: _normalizeIds(action.informed),
  };

  // ── Header : titre simple, pas de boutons ───────────────────────────────
  // Les CTA Annuler / Enregistrer / Enreg sans Notif vivent dans la sticky-
  // bottom de l'éditeur (RefactoUX/Actions 2026-05-20). Le header ne porte
  // que le titre + (via setContent ci-dessous) le breadcrumb.
  setHeader(esc(t('actionModal.title.edit', { id })), '');

  // ── Contenu : host éditeur unique ────────────────────────────────────────
  // Le breadcrumb est passé EN INTERNE à l'éditeur via stickyTopPrefixHtml
  // pour rester collé à la ligne Code/Titre/Priorité (un seul bloc sticky-top
  // dans l'éditeur). Évite que le breadcrumb scrolle hors écran pendant qu'on
  // navigue dans l'éditeur. Le wrapper expose une hauteur définie pour activer
  // le sticky/flex internes de l'éditeur.
  setContent(
    `<div style="height:calc(100vh - 56px - 48px);min-height:520px">
       <div id="ea-editor-host" style="height:100%"></div>
     </div>`,
  );

  const host = document.getElementById('ea-editor-host');
  if (!host) return;

  // Breadcrumb + CTA « Historique » (traçabilité). Le CTA (bouton secondaire)
  // ouvre la page de comparaison des versions de l'action ; il vit à côté du
  // fil d'Ariane, aligné à droite.
  const breadcrumbHtml = `<div style="display:flex;align-items:center;justify-content:space-between;gap:12px">
       <nav class="ea-breadcrumb" aria-label="${esc(t('pageAction.breadcrumbAria'))}" style="font-size:12px;color:var(--text2)">
         <a href="#actions" style="color:var(--accent);text-decoration:underline">${esc(t('vocabulary.action_plural'))}</a>
         <span style="color:var(--text3);margin:0 6px">›</span>
         <span style="color:var(--text2);font-weight:500">#${id}</span>
       </nav>
       <a href="#action/${id}/versions" class="btn btn-secondary" style="flex:none;font-size:12px;padding:4px 10px;text-decoration:none">${esc(t('pageActionVersions.cta'))}</a>
     </div>`;

  // Hash de retour : consommé une fois puis effacé. Posé par la page
  // appelante (render-page.ts via `sessionStorage` 'actionEditor:backHash')
  // pour préserver le contexte (filtres, mode rattachement réunion, etc.).
  // Fallback `#actions` si rien n'a été posé ou si sessionStorage est KO.
  const consumeBackHash = (): string => {
    try {
      const stored = sessionStorage.getItem('actionEditor:backHash');
      if (stored) {
        sessionStorage.removeItem('actionEditor:backHash');
        // Garde-fou : un backHash égal au hash courant produirait une
        // navigation no-op (le 1er clic sur "Fermer" ne ferait rien, il
        // faudrait re-cliquer). Cas observé quand le handler de liste,
        // attaché deux fois, ré-enregistre le hash de l'action elle-même.
        // On retombe alors sur la liste.
        if (stored.startsWith('#') && stored !== window.location.hash) return stored;
      }
    } catch {
      /* noop */
    }
    return '#actions';
  };

  // Garde "modifications non enregistrées" (audit UX 2026-06) : l'éditeur
  // suit son propre _hasUserChanges en interne mais ne l'expose pas. On
  // utilise un proxy input/change sur le host pour le routeur + beforeunload.
  let _actionEditDirty = false;
  host.addEventListener('input', () => {
    _actionEditDirty = true;
  });
  host.addEventListener('change', () => {
    _actionEditDirty = true;
  });
  setUnsavedGuard(() => _actionEditDirty);

  window.renderActionEditor(host, normalized as never, {
    users: users as never,
    statuses: statuses as never,
    projects: projects as never,
    stickyTopPrefixHtml: breadcrumbHtml,
    // Ouverture pleine page d'une action existante : blocs de liaison
    // (Projets / Comités / Organisation / Plans) repliés pour désencombrer,
    // avec le résumé des valeurs sélectionnées sous chaque titre.
    collapseLinkSections: true,
    onSaved: (saved: unknown) => {
      // Revient à la liste après sauvegarde réussie. Le toast de succès est
      // déjà déclenché à l'intérieur de l'éditeur. On pose le drapeau
      // "dernière action modifiée" pour que la liste anime la ligne au
      // retour (cf. utils/last-modified-anim.ts).
      clearUnsavedGuard();
      markLastModified('actions', id);
      // Mascotte « Professeur Ornaxion » : le volet (fixed, appendu au body)
      // survit à la navigation. La garde (mobile/préférence/cooldown/fréquence)
      // est appliquée dans le composant à partir de `saved.mascot_trigger`.
      void window.OrnaxionMascot?.onActionSaved(saved);
      window.location.hash = consumeBackHash();
    },
    onCancel: () => {
      // Garde anti-perte de saisie : confirme avant de quitter si modifié.
      void (async () => {
        if (!(await confirmLeaveIfDirty())) return;
        window.location.hash = consumeBackHash();
      })();
    },
  });
});

export {};
